import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../services/supabase';
import type { MainCategory } from '@flows.cash/types';

/**
 * Checks if a string is a valid UUID
 * @param str The string to check
 * @returns boolean indicating if the string is a valid UUID
 */
function isUuid(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

/**
 * Migrates categories from text IDs to UUID format
 * @param userId The user ID
 * @returns A mapping of old IDs to new UUIDs
 */
export async function migrateCategoriesToUuid(userId: string): Promise<void> {
  if (!userId) {
    console.error('No userId provided for migrateCategoriesToUuid');
    return;
  }

  try {
    console.log('Starting category migration');

    // Check if migration is needed
    const { data: categories, error: fetchError } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', userId);

    if (fetchError) throw fetchError;

    if (!categories || categories.length === 0) {
      console.log('No categories to migrate');
      return;
    }

    // Check if any category has a non-UUID id
    const needsMigration = categories.some(cat => !isUuid(cat.id));
    if (!needsMigration) {
      console.log('Categories already using UUIDs');
      return;
    }

    // Create mapping of old IDs to new UUIDs
    const idMapping: Record<string, string> = {};
    const updatedCategories = categories.map(cat => {
      const newId = generateUuid();
      idMapping[cat.id] = newId;
      return {
        ...cat,
        id: newId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    });

    // Delete old categories
    const { error: deleteError } = await supabase
      .from('categories')
      .delete()
      .eq('user_id', userId);

    if (deleteError) throw deleteError;

    // Insert new categories with UUIDs
    const { error: insertError } = await supabase
      .from('categories')
      .insert(updatedCategories);

    if (insertError) throw insertError;

    // Migrate budget categories to use new UUIDs
    await migrateBudgetCategories(userId, idMapping);

    console.log('Category migration completed successfully');
  } catch (error) {
    console.error('Error migrating categories:', error);
    throw error;
  }
}

/**
 * Validates and ensures all category IDs are valid UUIDs
 * @param categories The categories to validate
 * @returns Categories with valid UUID IDs
 */
export function validateCategoryUuids(categories: MainCategory[]): MainCategory[] {
  return categories.map(category => {
    // Check if the main category ID is a valid UUID
    const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(category.id);
    
    // If not a valid UUID, generate a new one
    const mainCategoryId = isValidUuid ? category.id : uuidv4();
    
    // Validate subcategories
    const subcategories = category.subcategories.map(subcategory => {
      const isValidSubUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(subcategory.id);
      return {
        ...subcategory,
        id: isValidSubUuid ? subcategory.id : uuidv4(),
        parentId: mainCategoryId
      };
    });
    
    return {
      ...category,
      id: mainCategoryId,
      subcategories
    };
  });
}

/**
 * Checks if a string is a valid UUID
 * @param id The ID to check
 * @returns True if the ID is a valid UUID
 */
export function isValidUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/**
 * Generates a new UUID
 * @returns A new UUID
 */
export function generateUuid(): string {
  return uuidv4();
}

/**
 * Migrates budget categories to use the new UUID format
 * @param userId The user ID
 * @param idMapping The mapping of old category IDs to new UUIDs
 */
export async function migrateBudgetCategories(userId: string, idMapping: Record<string, string>): Promise<void> {
  if (!userId || !idMapping) {
    console.error('No userId or idMapping provided for migrateBudgetCategories');
    return;
  }

  try {
    console.log('Starting budget category migration');

    // Fetch all budgets for the user
    const { data: budgets, error: fetchError } = await supabase
      .from('budgets')
      .select('*, budget_categories(category_id)')
      .eq('user_id', userId);

    if (fetchError) throw fetchError;

    if (!budgets || budgets.length === 0) {
      console.log('No budgets to migrate');
      return;
    }

    // Update budget categories
    for (const budget of budgets) {
      // Get the current category IDs
      const currentCategories = budget.budget_categories || [];
      
      // Delete existing budget categories
      const { error: deleteError } = await supabase
        .from('budget_categories')
        .delete()
        .eq('budget_id', budget.id);

      if (deleteError) throw deleteError;

      // Create new budget categories with updated category IDs
      const newCategories = currentCategories.map((bc: any) => ({
        budget_id: budget.id,
        category_id: idMapping[bc.category_id] || bc.category_id, // Use new UUID if available, otherwise keep existing
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      if (newCategories.length > 0) {
        const { error: insertError } = await supabase
          .from('budget_categories')
          .insert(newCategories);

        if (insertError) throw insertError;
      }
    }

    console.log('Budget category migration completed successfully');
  } catch (error) {
    console.error('Error migrating budget categories:', error);
    throw error;
  }
} 